import type {
	EasyInputMessage,
	ResponseInputItem,
	Tool as ResponseTool,
} from "openai/resources/responses/responses";
import type { Either } from "fp-ts/lib/Either.js";

export type JsonSchema = {
	type: string | string[];
	description?: string;
	properties?: Record<string, unknown>;
	required?: string[];
	additionalProperties?: boolean;
	items?: JsonSchema;
};

export type ToolContext = {
	viewRoot?: Element;
	document?: Document;
	window?: Window;
	signal?: AbortSignal;
};

export type ToolAction = (args: unknown, ctx: ToolContext) => Promise<unknown> | unknown;

export type TokenCounter = (messages: Message[], model?: string) => number | Promise<number>;

export type Callable = {
	kind: "tool" | "skill";
	name: string;
	callName: string;
	description?: string;
	toToolDefinition: () => ToolDefinition;
	formatForList: () => string;
};


export type ToolCall = {
	id?: string;
	name: string;
	args: unknown;
};

export type Message = EasyInputMessage | ResponseInputItem;
export type ToolDefinition = ResponseTool;

export type SkillCallArgs = {
	task: string;
	history?: EasyInputMessage[];
};

export type RunAgentOptions = {
	skillDepth?: number;
	callableListMessage?: Message | null;
	skipActiveRuns?: boolean;
	tokenCounter?: TokenCounter;
	contextWindowTokens?: number;
	compactThreshold?: number;
	model?: string;
};

export enum AgentStatusKind {
	Thinking = "thinking",
	CallingTool = "calling_tool",
	ToolResult = "tool_result",
	Done = "done",
}

export type AgentStatus = {
	kind: AgentStatusKind;
	label?: string;
	toolName?: string;
};

export type AgentStatusEvent = { type: "status"; status: AgentStatus };

export type ToolStartBase = {
	type: "tool.start";
	name: string;
	args: unknown;
	callId?: string;
};

export type SkillToolStart = ToolStartBase & {
	isSkill: true;
	depth: number;
	input: SkillCallArgs;
};

export type ToolStart = ToolStartBase | (ToolStartBase & { isSkill?: false }) | SkillToolStart;

export type ToolEndBase = {
	type: "tool.end";
	name: string;
	result: unknown;
};

export type SkillToolEnd = ToolEndBase & {
	isSkill: true;
	depth: number;
};

export type ToolEnd = ToolEndBase | (ToolEndBase & { isSkill?: false }) | SkillToolEnd;

export type AgentEvent =
	| { type: "message"; content: string }
	| { type: "message.delta"; delta: string }
	| { type: "thinking"; summary: string }
	| { type: "thinking.delta"; delta: string }
	| ToolStart
	| ToolEnd
	| { type: "artifact"; name: string; data: unknown }
	| { type: "done" };

export type AgentStreamEvent = Either<Error, AgentEvent>;
export type AgentStatusStreamEvent = Either<Error, AgentEvent | AgentStatusEvent>;

export type AgentGenerate = (
	messages: Message[],
	tools?: ToolDefinition[],
	signal?: AbortSignal
) => AsyncIterable<AgentStreamEvent>;

export type ChatRole = "user" | "assistant";

export type ChatMessage = {
	role: ChatRole;
	text: string;
};

export type ChatStatus =
	| { kind: "thinking"; label?: string }
	| { kind: "calling_tool"; toolName?: string; label?: string }
	| { kind: "tool_result"; toolName?: string; label?: string }
	| { kind: "done" };

export type ChatUiStatusLabels = {
	thinking: string;
	callingTool: string;
	toolResult: string;
};

export type ChatUiOptions = {
	container: HTMLElement;
	initialMessages?: ChatMessage[];
	statusLabels?: Partial<ChatUiStatusLabels>;
};

export type ChatUiController = {
	addUserMessage: (text: string) => void;
	appendAssistantDelta: (delta: string) => void;
	finalizeAssistantMessage: (text?: string) => void;
	setStatus: (status: ChatStatus | null) => void;
	setThinkingSummary: (summary: string) => void;
};

export type SkillModuleMap = Record<string, string>;

export type McpTool = {
	name: string;
	description?: string;
	inputSchema?: JsonSchema;
	outputSchema?: JsonSchema;
};

export type McpToolResult = {
	content: unknown;
	metadata?: unknown;
};

export type McpHttpClientOptions = {
	baseUrl: string;
	bearerToken?: string;
};

export type McpClient = {
	listTools: () => Promise<McpTool[]>;
	callTool: (name: string, args: unknown) => Promise<McpToolResult>;
};
